import { cn } from '@/lib/utils'

/* Teams app screens in CSS, drawn at true iPhone size (393×852) and shrunk via IPhone's `scale`. */

const PHONE_WIDTH = 393
const PHONE_HEIGHT = 852

const DRIVERS = [
  { name: 'Steve Johnson', used: '300 / 450 gal', pct: '67%' },
  { name: 'Susan White', used: '120 / 450 gal', pct: '27%' },
  { name: 'Randy Smith', used: '0 / 450 gal', pct: '0%' },
]

const LIMIT_PRESETS = ['250', '450', '600', '800']

const CARD = 'rounded-2xl bg-white shadow-[0_2px_8px_rgba(0,0,0,0.06)]'

function ScreenTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-[30px] font-semibold tracking-[-0.01em] text-[#101013]">{children}</div>
}

/** iPhone bezel with status bar and home indicator, occupying exactly its scaled size in layout. */
export function IPhone({
  children,
  scale,
  className,
}: {
  children: React.ReactNode
  scale: number
  className?: string
}) {
  return (
    // Fixed px box + transform instead of CSS zoom: a browser that ignored zoom would render the full 393px phone.
    <div
      aria-hidden="true"
      className={cn('flex-none overflow-visible', className)}
      style={{ width: PHONE_WIDTH * scale, height: PHONE_HEIGHT * scale }}
    >
      <div
        className="relative box-border rounded-[56px] bg-[#101013] p-[11px] shadow-[0_24px_60px_rgba(16,16,19,0.18),0_4px_14px_rgba(16,16,19,0.12)]"
        style={{
          width: PHONE_WIDTH,
          height: PHONE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-[45px] bg-[#F4F7F9] text-left">
          <div className="absolute top-[11px] left-1/2 h-9 w-[122px] -translate-x-1/2 rounded-[18px] bg-[#101013]" />
          <div className="flex h-[54px] flex-none items-center justify-between pt-1 pr-8 pl-10 text-base font-semibold text-[#101013]">
            <span>9:41</span>
            <span className="flex items-center gap-1.5">
              <span className="h-[11px] w-[18px] rounded-[2px] bg-[#101013]" />
              <span className="box-border flex h-3 w-[25px] rounded-[4px] border-[1.5px] border-[#101013] p-[1.5px]">
                <span className="flex-1 rounded-[1.5px] bg-[#101013]" />
              </span>
            </span>
          </div>
          {children}
          <div className="absolute bottom-[9px] left-1/2 h-[5px] w-[136px] -translate-x-1/2 rounded-[3px] bg-[#101013]" />
        </div>
      </div>
    </div>
  )
}

/** Weekly savings plus each driver's usage against their limit. */
export function TeamScreen() {
  return (
    <div className="flex flex-col gap-4 px-5 pt-3.5">
      <ScreenTitle>Randy&apos;s Team</ScreenTitle>
      <div className={cn(CARD, 'flex flex-col gap-1.5 p-5')}>
        <span className="text-[15px] font-medium text-[#555962]">Saved this week</span>
        <span className="text-[44px] font-bold tracking-[-0.02em] text-[#101013]">$129.00</span>
        <span className="text-sm text-[#555962]">
          300 gal · <span className="font-bold text-[#00AA6D]">43¢ off</span> avg
        </span>
      </div>
      <div className="px-0.5 pt-1.5 text-[15px] font-medium text-[#555962]">Drivers</div>
      <div className={cn(CARD, 'flex flex-col')}>
        {DRIVERS.map((driver) => (
          <div
            key={driver.name}
            className="flex flex-col gap-2.5 border-b border-[#E6EBEF] px-[18px] py-4 last:border-b-0"
          >
            <div className="flex items-baseline justify-between">
              <span className="text-[17px] font-medium text-[#101013]">{driver.name}</span>
              <span className="text-sm text-[#555962]">{driver.used}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-[3px] bg-[#E6EBEF]">
              <div className="h-1.5 rounded-[3px] bg-[#002CB1]" style={{ width: driver.pct }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

/** Inviting a driver with a weekly gallon limit. */
export function InviteScreen() {
  return (
    <div className="flex flex-col gap-[18px] px-5 pt-3.5">
      <ScreenTitle>Invite driver</ScreenTitle>
      <div className="flex flex-col gap-2">
        <span className="text-[15px] font-medium text-[#555962]">Driver&apos;s phone</span>
        <div className={cn(CARD, 'rounded-xl p-[18px] text-lg text-[#101013]')}>(317) 962-4471</div>
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-[15px] font-medium text-[#555962]">Weekly limit</span>
        <div className="flex items-baseline justify-between rounded-xl border-2 border-[#002CB1] bg-white px-[18px] py-4">
          <span className="text-[44px] font-bold tracking-[-0.02em] text-[#101013]">450</span>
          <span className="text-base text-[#555962]">gal / week</span>
        </div>
        <div className="flex gap-2">
          {LIMIT_PRESETS.map((preset) => (
            <span
              key={preset}
              className={cn(
                'flex-1 rounded-[10px] py-3 text-center text-base font-medium',
                preset === '450' ? 'bg-[#002CB1] text-white' : 'bg-white text-[#101013]'
              )}
            >
              {preset}
            </span>
          ))}
        </div>
      </div>
      <div className="text-sm leading-normal text-[#555962]">Resets every Monday. Change it any time.</div>
      <div className="mt-2 rounded-[36px] bg-[#002CB1] p-[18px] text-center text-[17px] font-medium text-white">
        Send invite
      </div>
    </div>
  )
}

/** The one-time code a driver enters at the pump. */
export function CodeScreen() {
  return (
    <div className="flex flex-col gap-4 px-5 pt-3.5">
      <ScreenTitle>Fuel code</ScreenTitle>
      <div className="flex flex-col items-center gap-2.5 rounded-2xl bg-[#D4F4FA] px-5 py-7">
        <span className="text-base font-medium text-[#101013]">Enter at pump 7</span>
        <span className="text-[50px] font-bold tracking-[0.06em] text-[#101013]">4821 7730</span>
        <span className="text-[15px] font-medium text-[#F58220]">Expires in 30 mins</span>
      </div>
      <div className={cn(CARD, 'flex flex-col gap-3.5 px-5 py-[18px]')}>
        <div className="flex items-baseline justify-between">
          <span className="text-base font-medium text-[#101013]">Your price</span>
          <span className="text-2xl font-bold text-[#101013]">
            $3.79<span className="text-[15px] font-normal text-[#555962]"> /gal</span>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[15px] text-[#555962]">Discount</span>
          <span className="rounded-[3px] bg-[#E2F6EE] px-[9px] py-1 text-sm font-bold text-[#00AA6D]">
            43¢ off
          </span>
        </div>
      </div>
      <div className="px-0.5 text-sm leading-normal text-[#555962]">
        No card at the pump. Charged to your team card when the fill ends.
      </div>
    </div>
  )
}
